import React, { useState, useMemo, useRef, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Area, ScatterChart, Scatter } from 'recharts';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import FileUploader from './FileUploader';

// Компонент CIE діаграми з кольоровим фоном
const CIEDiagram = ({ oledX, oledY, whiteX, whiteY, dominantWavelength }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    // Параметри відображення
    const padding = 60;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;
    const xMax = 0.8;
    const yMax = 0.9;
    
    // Функція для переведення координат CIE в пікселі
    const toPixelX = (x) => padding + (x / xMax) * plotWidth;
    const toPixelY = (y) => height - padding - (y / yMax) * plotHeight;
    
    // Очистити canvas
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Спектральний локус (точні координати CIE 1931)
    const spectralLocus = [
      { x: 0.1741, y: 0.0050, wl: 380 },
      { x: 0.1738, y: 0.0050, wl: 390 },
      { x: 0.1733, y: 0.0048, wl: 400 },
      { x: 0.1710, y: 0.0048, wl: 410 },
      { x: 0.1669, y: 0.0051, wl: 420 },
      { x: 0.1528, y: 0.0058, wl: 430 },
      { x: 0.1382, y: 0.0074, wl: 440 },
      { x: 0.1070, y: 0.0155, wl: 450 },
      { x: 0.0816, y: 0.0283, wl: 460 },
      { x: 0.0481, y: 0.0600, wl: 470 },
      { x: 0.0200, y: 0.1327, wl: 480 },
      { x: 0.0088, y: 0.2950, wl: 490 },
      { x: 0.0030, y: 0.5384, wl: 500 },
      { x: 0.0049, y: 0.7502, wl: 510 },
      { x: 0.0232, y: 0.8563, wl: 520 },
      { x: 0.0743, y: 0.9238, wl: 530 },
      { x: 0.1547, y: 0.9334, wl: 540 },
      { x: 0.2296, y: 0.8927, wl: 550 },
      { x: 0.3016, y: 0.8130, wl: 560 },
      { x: 0.3731, y: 0.7170, wl: 570 },
      { x: 0.4441, y: 0.6123, wl: 580 },
      { x: 0.5125, y: 0.5093, wl: 590 },
      { x: 0.5752, y: 0.4152, wl: 600 },
      { x: 0.6270, y: 0.3414, wl: 610 },
      { x: 0.6658, y: 0.2887, wl: 620 },
      { x: 0.6915, y: 0.2548, wl: 630 },
      { x: 0.7079, y: 0.2327, wl: 640 },
      { x: 0.7190, y: 0.2170, wl: 650 },
      { x: 0.7260, y: 0.2059, wl: 660 },
      { x: 0.7300, y: 0.1977, wl: 670 },
      { x: 0.7334, y: 0.1915, wl: 680 },
      { x: 0.7344, y: 0.1866, wl: 690 },
      { x: 0.7347, y: 0.1828, wl: 700 },
    ];
    
    // Малюємо кольоровий градієнт усередині спектрального локусу
    const imageData = ctx.createImageData(plotWidth, plotHeight);
    const data = imageData.data;
    
    for (let px = 0; px < plotWidth; px++) {
      for (let py = 0; py < plotHeight; py++) {
        const x = (px / plotWidth) * xMax;
        const y = ((plotHeight - py) / plotHeight) * yMax;
        
        if (isInsideSpectralLocus(x, y, spectralLocus)) {
          const rgb = chromaticityToRGB(x, y);
          const index = (py * plotWidth + px) * 4;
          data[index] = rgb[0];
          data[index + 1] = rgb[1];
          data[index + 2] = rgb[2];
          data[index + 3] = 255;
        }
      }
    }
    
    ctx.putImageData(imageData, padding, padding);
    
    // Малюємо сітку
    ctx.strokeStyle = '#dddddd';
    ctx.lineWidth = 1;
    
    for (let i = 0; i <= 8; i++) {
      const x = i * 0.1;
      const px = toPixelX(x);
      ctx.beginPath();
      ctx.moveTo(px, padding);
      ctx.lineTo(px, height - padding);
      ctx.stroke();
    }
    
    for (let i = 0; i <= 9; i++) {
      const y = i * 0.1;
      const py = toPixelY(y);
      ctx.beginPath();
      ctx.moveTo(padding, py);
      ctx.lineTo(width - padding, py);
      ctx.stroke();
    }
    
    // Малюємо межу спектрального локусу
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 3;
    ctx.beginPath();
    
    spectralLocus.forEach((point, i) => {
      const px = toPixelX(point.x);
      const py = toPixelY(point.y);
      if (i === 0) {
        ctx.moveTo(px, py);
      } else {
        ctx.lineTo(px, py);
      }
    });
    
    // Замикаємо лінією пурпурних кольорів
    ctx.lineTo(toPixelX(spectralLocus[0].x), toPixelY(spectralLocus[0].y));
    ctx.stroke();
    
    // Додаємо мітки довжин хвиль
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 11px Arial';
    
    const labelPoints = [380, 470, 490, 510, 520, 530, 540, 550, 560, 570, 580, 590, 600, 620, 650, 700];
    labelPoints.forEach(wl => {
      const point = spectralLocus.find(p => p.wl === wl);
      if (point) {
        const px = toPixelX(point.x);
        const py = toPixelY(point.y);
        
        // Визначаємо зсув тексту залежно від позиції
        let offsetX = 8, offsetY = -8;
        if (wl < 500) {
          offsetX = -25;
          offsetY = 0;
        } else if (wl > 600) {
          offsetX = 8;
          offsetY = 5;
        }
        
        ctx.fillText(`${wl}`, px + offsetX, py + offsetY);
      }
    });
    
    // Малюємо лінію від білої точки до OLED
    ctx.strokeStyle = '#666666';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(toPixelX(whiteX), toPixelY(whiteY));
    ctx.lineTo(toPixelX(oledX), toPixelY(oledY));
    ctx.stroke();
    ctx.setLineDash([]);
    
    // Позначаємо букви a та b для чистоти кольору
    const midX = (whiteX + oledX) / 2;
    const midY = (whiteY + oledY) / 2;
    ctx.fillStyle = '#333';
    ctx.font = 'italic 12px Arial';
    ctx.fillText('a', toPixelX(midX) - 5, toPixelY(midY) - 5);
    
    // Малюємо точку OLED
    const oledPx = toPixelX(oledX);
    const oledPy = toPixelY(oledY);
    
    // Тінь
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    
    ctx.fillStyle = '#4caf50';
    ctx.beginPath();
    ctx.arc(oledPx, oledPy, 10, 0, 2 * Math.PI);
    ctx.fill();
    
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Підпис OLED
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 14px Arial';
    ctx.fillText('Джерело (x,y)', oledPx + 15, oledPy - 8);
    ctx.font = '12px Arial';
    ctx.fillText(`(${oledX.toFixed(3)}, ${oledY.toFixed(3)})`, oledPx + 15, oledPy + 6);
    
    // Малюємо білу точку (D65)
    const whitePx = toPixelX(whiteX);
    const whitePy = toPixelY(whiteY);
    
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(whitePx, whitePy, 8, 0, 2 * Math.PI);
    ctx.fill();
    
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Підпис білої точки
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 12px Arial';
    ctx.fillText('Біла точка', whitePx - 35, whitePy - 12);
    
    // Малюємо осі
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    
    // Вісь X
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(width - padding + 10, height - padding);
    ctx.stroke();
    
    // Стрілка X
    ctx.beginPath();
    ctx.moveTo(width - padding + 10, height - padding);
    ctx.lineTo(width - padding + 5, height - padding - 5);
    ctx.lineTo(width - padding + 5, height - padding + 5);
    ctx.closePath();
    ctx.fill();
    
    // Вісь Y
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(padding, padding - 10);
    ctx.stroke();
    
    // Стрілка Y
    ctx.beginPath();
    ctx.moveTo(padding, padding - 10);
    ctx.lineTo(padding - 5, padding - 5);
    ctx.lineTo(padding + 5, padding - 5);
    ctx.closePath();
    ctx.fill();
    
    // Мітки на осях
    ctx.fillStyle = '#000000';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    
    for (let i = 0; i <= 8; i++) {
      const x = i * 0.1;
      const px = toPixelX(x);
      ctx.fillText(x.toFixed(1), px, height - padding + 20);
    }
    
    ctx.textAlign = 'right';
    for (let i = 0; i <= 9; i++) {
      const y = i * 0.1;
      const py = toPixelY(y);
      ctx.fillText(y.toFixed(1), padding - 10, py + 4);
    }
    
    // Назви осей
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('x-координата кольору', width / 2, height - 10);
    
    ctx.save();
    ctx.translate(15, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('y-координата кольору', 0, 0);
    ctx.restore();
    
  }, [oledX, oledY, whiteX, whiteY]);
  
  // Перевірка чи точка всередині спектрального локусу
  function isInsideSpectralLocus(x, y, locus) {
    // Метод трасування променя
    let inside = false;
    for (let i = 0, j = locus.length - 1; i < locus.length; j = i++) {
      const xi = locus[i].x, yi = locus[i].y;
      const xj = locus[j].x, yj = locus[j].y;
      
      const intersect = ((yi > y) !== (yj > y))
          && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    
    // Додаємо лінію пурпурних кольорів
    if (!inside) {
      const firstPoint = locus[0];
      const lastPoint = locus[locus.length - 1];
      
      // Перевірка чи точка нижче лінії пурпурних
      if (y < firstPoint.y && x >= firstPoint.x && x <= lastPoint.x) {
        const slope = (lastPoint.y - firstPoint.y) / (lastPoint.x - firstPoint.x);
        const yOnLine = firstPoint.y + slope * (x - firstPoint.x);
        if (y >= yOnLine) inside = true;
      }
    }
    
    return inside;
  }
  
  // Конвертація CIE xy в RGB
  function chromaticityToRGB(x, y) {
    const Y = 1.0;
    const X = y !== 0 ? (Y / y) * x : 0;
    const Z = y !== 0 ? (Y / y) * (1 - x - y) : 0;
    
    // XYZ to RGB matrix (sRGB D65)
    let r =  3.2406 * X - 1.5372 * Y - 0.4986 * Z;
    let g = -0.9689 * X + 1.8758 * Y + 0.0415 * Z;
    let b =  0.0557 * X - 0.2040 * Y + 1.0570 * Z;
    
    // Гама-корекція sRGB
    const gammaCorrect = (c) => {
      if (c <= 0.0031308) return 12.92 * c;
      return 1.055 * Math.pow(c, 1/2.4) - 0.055;
    };
    
    r = gammaCorrect(r);
    g = gammaCorrect(g);
    b = gammaCorrect(b);
    
    // Нормалізація якщо значення виходять за межі
    const maxVal = Math.max(r, g, b);
    if (maxVal > 1) {
      r /= maxVal;
      g /= maxVal;
      b /= maxVal;
    }
    
    // Обмеження та конвертація в 0-255
    r = Math.max(0, Math.min(255, Math.round(255 * r)));
    g = Math.max(0, Math.min(255, Math.round(255 * g)));
    b = Math.max(0, Math.min(255, Math.round(255 * b)));
    
    return [r, g, b];
  }
  
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas 
        ref={canvasRef} 
        width={700} 
        height={700} 
        style={{ 
          width: '100%', 
          height: '100%',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      />
      <div style={{ 
        marginTop: '15px', 
        fontSize: '13px', 
        color: '#555',
        textAlign: 'center',
        fontStyle: 'italic'
      }}>
        Діаграма колірності CIE 1931 • Домінуюча довжина хвилі: <strong>{dominantWavelength} нм</strong>
      </div>
    </div>
  );
};

const OLEDConspectFull = () => {
  const [uploadedData, setUploadedData] = useState(null);
  
  // Стилі (такі ж як раніше)
  const styles = {
    conspect: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: '40px 60px',
      background: 'white',
      minHeight: '100vh',
      fontFamily: 'Georgia, serif',
      lineHeight: '1.8',
      fontSize: '16px',
      color: '#2c3e50'
    },
    title: {
      textAlign: 'center',
      marginBottom: '10px',
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#2c3e50'
    },
    subtitle: {
      textAlign: 'center',
      marginBottom: '5px',
      fontSize: '18px',
      color: '#34495e'
    },
    section: {
      marginTop: '40px',
      marginBottom: '30px'
    },
    h2: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginTop: '50px',
      marginBottom: '20px',
      color: '#2c3e50',
      borderBottom: '2px solid #3498db',
      paddingBottom: '10px'
    },
    h3: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginTop: '30px',
      marginBottom: '15px',
      color: '#34495e'
    },
    paragraph: {
      marginBottom: '15px',
      textAlign: 'justify',
      textIndent: '30px'
    },
    formula: {
      margin: '25px 0',
      padding: '20px',
      background: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #dee2e6'
    },
    calculation: {
      margin: '20px 0',
      padding: '15px 20px',
      background: '#e8f5e9',
      borderRadius: '6px',
      borderLeft: '4px solid #4caf50'
    },
    result: {
      margin: '20px 0',
      padding: '15px 20px',
      background: '#fff3cd',
      borderRadius: '6px',
      borderLeft: '4px solid #ffc107',
      fontSize: '18px',
      fontWeight: 'bold'
    },
    graph: {
      margin: '30px 0',
      padding: '20px',
      background: '#f8f9fa',
      borderRadius: '8px'
    }
  };

  // Демо-дані
  const defaultVachData = [
    { voltage: 0.0, current1: 1.681e-9, current2: 3.699e-9 },
    { voltage: 2.6, current1: 31.88e-9, current2: 1.588e-9 },
    { voltage: 3.0, current1: 1.267e-6, current2: 42.53e-9 },
    { voltage: 4.0, current1: 26.92e-6, current2: 877.8e-9 },
    { voltage: 5.0, current1: 81.61e-6, current2: 2.559e-6 },
    { voltage: 6.0, current1: 180.3e-6, current2: 5.371e-6 },
    { voltage: 7.0, current1: 349.4e-6, current2: 9.737e-6 },
    { voltage: 8.0, current1: 625.3e-6, current2: 16.07e-6 },
    { voltage: 9.0, current1: 1.060e-3, current2: 24.79e-6 },
    { voltage: 10.0, current1: 1.716e-3, current2: 36.14e-6 }
  ];

  const vachData = useMemo(() => {
    const data = uploadedData || defaultVachData;
    console.log('🔄 vachData recalculated, points:', data.length);
    console.log('📈 First data point:', data[0]);
    return data;
  }, [uploadedData]);

  const [spectralData] = useState([
    { wavelength: 380, intensity: 0, intensity_eye: 0.0001, intensity_photodiode: 0 },
    { wavelength: 420, intensity: 0, intensity_eye: 0.004, intensity_photodiode: 0 },
    { wavelength: 460, intensity: 0.15, intensity_eye: 0.06, intensity_photodiode: 0.08 },
    { wavelength: 480, intensity: 0.68, intensity_eye: 0.139, intensity_photodiode: 0.4 },
    { wavelength: 500, intensity: 1.85, intensity_eye: 0.323, intensity_photodiode: 1.1 },
    { wavelength: 520, intensity: 2.85, intensity_eye: 0.71, intensity_photodiode: 1.8 },
    { wavelength: 540, intensity: 2.45, intensity_eye: 0.954, intensity_photodiode: 1.5 },
    { wavelength: 560, intensity: 1.5, intensity_eye: 0.995, intensity_photodiode: 0.9 },
    { wavelength: 580, intensity: 0.6, intensity_eye: 0.87, intensity_photodiode: 0.35 },
    { wavelength: 600, intensity: 0.15, intensity_eye: 0.631, intensity_photodiode: 0.09 },
    { wavelength: 650, intensity: 0.01, intensity_eye: 0.107, intensity_photodiode: 0.005 },
    { wavelength: 720, intensity: 0, intensity_eye: 0.001, intensity_photodiode: 0 },
    { wavelength: 770, intensity: 0, intensity_eye: 0.00001, intensity_photodiode: 0 }
  ]);

  // Розрахунки ефективностей та інших параметрів
  const efficiencyData = useMemo(() => {
    console.log('⚙️ Recalculating efficiencyData...');
    const area = 0.16; // cm²
    const data = vachData.filter(d => d.voltage >= 2.6).map(d => {
      const current = Math.abs(d.current1);
      const power = d.voltage * current;
      const currentDensity = (current * 1000) / area; // mA/cm²
      
      // Спрощені розрахунки для демонстрації
      const luminance = currentDensity * 0.5; // cd/m² (припущення: 0.5 cd/A)
      const currentEfficiency = luminance / currentDensity; // cd/A
      const powerEfficiency = (luminance / power) * Math.PI; // lm/W
      const eqe = currentEfficiency * 0.3; // %
      
      return {
        voltage: d.voltage,
        currentDensity,
        luminance,
        currentEfficiency,
        powerEfficiency,
        eqe
      };
    });
    console.log('✅ efficiencyData calculated, points:', data.length);
    return data;
  }, [vachData]);

  // Температурні дані (симуляція)
  const temperatureData = useMemo(() => {
    const temps = [25, 50, 75, 100, 125, 150];
    return temps.map(temp => ({
      temperature: temp,
      efficiency: 100 * Math.exp(-0.003 * (temp - 25)),
      lifetime: 10000 * Math.exp(-0.01 * (temp - 25))
    }));
  }, []);

  const calculations = useMemo(() => {
    const trapezoidalIntegrate = (data, getY) => {
      let sum = 0;
      for (let i = 0; i < data.length - 1; i++) {
        const x1 = data[i].wavelength;
        const x2 = data[i + 1].wavelength;
        const y1 = getY(data[i]);
        const y2 = getY(data[i + 1]);
        sum += (y1 + y2) * (x2 - x1) / 2;
      }
      return sum;
    };

    const numeratorKr = trapezoidalIntegrate(spectralData, d => d.intensity * d.intensity_eye);
    const denominatorKr = trapezoidalIntegrate(spectralData, d => d.intensity);
    const Kr = 683 * (numeratorKr / denominatorKr);
    const FF = trapezoidalIntegrate(spectralData, d => d.intensity * d.intensity_photodiode) / denominatorKr;

    // CIE розрахунки (спрощені)
    const X = 42.5;
    const Y = 85.3;
    const Z = 38.2;
    const sum = X + Y + Z;
    const x = X / sum;
    const y = Y / sum;

    const xw = 0.3127;
    const yw = 0.3290;
    const a = Math.sqrt((x - xw) ** 2 + (y - yw) ** 2);
    const purity = 0.785;

    const n = (x - 0.3320) / (0.1858 - y);
    const CCT = Math.round(449 * n ** 3 + 3525 * n ** 2 + 6823.3 * n + 5520.33);

    return { Kr, FF, X, Y, Z, x, y, purity, CCT, numeratorKr, denominatorKr };
  }, [spectralData]);

  return (
    <div style={styles.conspect}>
      {/* Титульна сторінка */}
      <div style={{ textAlign: 'center', marginBottom: '60px', marginTop: '40px' }}>
        <h1 style={{ ...styles.title, fontSize: '32px' }}>ЛАБОРАТОРНА РОБОТА №2</h1>
        <h2 style={{ ...styles.subtitle, fontSize: '24px', marginTop: '20px' }}>
          Дослідження характеристик органічних<br/>світловипромінюючих структур (OLED)
        </h2>
        <div style={{ marginTop: '60px', fontSize: '16px', lineHeight: '2' }}>
          <p>Національний університет "Львівська Політехніка"</p>
          <p>Кафедра електронної інженерії</p>
          <p style={{ marginTop: '40px' }}>Органічна електроніка</p>
        </div>
      </div>

      {/* Завантаження даних */}
      <FileUploader onDataLoaded={setUploadedData} />

      {/* Мета */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Мета роботи</h2>
        <p style={styles.paragraph}>
          Вивчити принципи роботи органічних світловипромінюючих діодів (OLED), освоїти методику 
          вимірювання їх основних характеристик та навчитися проводити розрахунки фотометричних, 
          колориметричних параметрів та ефективностей випромінювання.
        </p>
      </div>

      {/* Теорія - скорочена версія */}
      <div style={styles.section}>
        <h2 style={styles.h2}>1. Теоретичні відомості</h2>
        <p style={styles.paragraph}>
          OLED — органічні світловипромінюючі діоди, що базуються на електролюмінесценції 
          органічних напівпровідників. При прикладанні напруги відбувається інжекція носіїв 
          заряду, їх рекомбінація з утворенням екситонів та випромінювання фотонів.
        </p>
      </div>

      {/* Крок 1: Kr */}
      <div style={styles.section}>
        <h2 style={styles.h2}>2. Світлова ефективність Kr</h2>
        <p style={styles.paragraph}>
          Світлова ефективність показує скільки люменів світлового потоку генерується одним 
          ватом електричної потужності з урахуванням спектральної чутливості людського ока.
        </p>
        <div style={styles.formula}>
          <BlockMath math="K_r = K_m \times \frac{\int \Phi_e(\lambda) \cdot V(\lambda) \, d\lambda}{\int \Phi_e(\lambda) \, d\lambda}" />
        </div>
        <div style={styles.result}>
          <InlineMath math={`K_r = ${calculations.Kr.toFixed(2)} \\text{ лм/Вт}`} />
        </div>

        <div style={styles.graph}>
          <h4 style={{ marginBottom: '15px', textAlign: 'center' }}>
            Графік 1. Спектр OLED та чутливість ока
          </h4>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={spectralData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="wavelength" label={{ value: 'λ, нм', position: 'insideBottom', offset: -5 }} />
              <YAxis label={{ value: 'Інтенсивність', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="intensity" fill="#4caf50" fillOpacity={0.3} stroke="#2e7d32" name="OLED" />
              <Line type="monotone" dataKey="intensity_eye" stroke="#f44336" strokeWidth={2} dot={false} name="V(λ)" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ВАХ */}
      <div style={styles.section}>
        <h2 style={styles.h2}>3. Вольт-амперна характеристика</h2>
        <p style={styles.paragraph}>
          ВАХ демонструє залежність струму від напруги та дозволяє визначити порогову напругу 
          включення OLED (~2.6 В для даного зразка).
        </p>
        <div style={styles.graph}>
          <h4 style={{ marginBottom: '15px', textAlign: 'center' }}>Графік 2. ВАХ (логарифмічна шкала)</h4>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={vachData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="voltage" label={{ value: 'Напруга, В', position: 'insideBottom', offset: -5 }} />
              <YAxis scale="log" domain={[1e-10, 1]} label={{ value: 'Струм, А', angle: -90, position: 'insideLeft' }} />
              <Tooltip formatter={(value) => value.toExponential(2)} />
              <Legend />
              <Line type="monotone" dataKey="current1" stroke="#2196f3" strokeWidth={2} dot={{ r: 2 }} name="Ch1" />
              <Line type="monotone" dataKey="current2" stroke="#ff9800" strokeWidth={2} dot={{ r: 2 }} name="Ch2" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Ефективності */}
      <div style={styles.section}>
        <h2 style={styles.h2}>4. Ефективності OLED</h2>
        
        <h3 style={styles.h3}>4.1. Current Efficiency (Струмова ефективність)</h3>
        <p style={styles.paragraph}>
          Струмова ефективність показує скільки кандел на квадратний метр яскравості генерується 
          одним ампером струму. Вимірюється у кд/А.
        </p>
        <div style={styles.formula}>
          <BlockMath math="\eta_I = \frac{L}{J} \text{ [cd/A]}" />
        </div>

        <h3 style={styles.h3}>4.2. Power Efficiency (Енергетична ефективність)</h3>
        <p style={styles.paragraph}>
          Енергетична ефективність визначає скільки люменів світлового потоку генерується одним 
          ватом електричної потужності. Вимірюється у лм/Вт.
        </p>
        <div style={styles.formula}>
          <BlockMath math="\eta_P = \frac{\Phi_v}{P} = \frac{L \cdot A}{V \cdot I} \text{ [lm/W]}" />
        </div>

        <h3 style={styles.h3}>4.3. External Quantum Efficiency (Зовнішня квантова ефективність)</h3>
        <p style={styles.paragraph}>
          EQE показує відношення кількості фотонів, що виходять з пристрою, до кількості 
          інжектованих носіїв заряду. Вимірюється у відсотках.
        </p>
        <div style={styles.formula}>
          <BlockMath math="\eta_{EQE} = \frac{N_{photons}}{N_{electrons}} \times 100\%" />
        </div>

        <div style={styles.graph}>
          <h4 style={{ marginBottom: '15px', textAlign: 'center' }}>Графік 3. Залежності ефективностей від напруги</h4>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={efficiencyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="voltage" label={{ value: 'Напруга, В', position: 'insideBottom', offset: -5 }} />
              <YAxis label={{ value: 'Ефективність', angle: -90, position: 'insideLeft' }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="currentEfficiency" stroke="#4caf50" strokeWidth={2} name="ηᵢ (cd/A)" />
              <Line type="monotone" dataKey="eqe" stroke="#f44336" strokeWidth={2} name="EQE (%)" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.graph}>
          <h4 style={{ marginBottom: '15px', textAlign: 'center' }}>Графік 4. Яскравість vs Густина струму</h4>
          <ResponsiveContainer width="100%" height={350}>
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="currentDensity" name="J" label={{ value: 'Густина струму, mA/cm²', position: 'insideBottom', offset: -5 }} />
              <YAxis type="number" dataKey="luminance" name="L" label={{ value: 'Яскравість, cd/m²', angle: -90, position: 'insideLeft' }} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} />
              <Scatter name="L-J характеристика" data={efficiencyData} fill="#2196f3" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Температурна залежність */}
      <div style={styles.section}>
        <h2 style={styles.h2}>5. Температурна залежність</h2>
        <p style={styles.paragraph}>
          Підвищення температури зменшує ефективність OLED через термічне гасіння екситонів 
          та деградацію органічних матеріалів. Також скорочується час життя пристрою.
        </p>
        
        <div style={styles.graph}>
          <h4 style={{ marginBottom: '15px', textAlign: 'center' }}>Графік 5. Вплив температури на параметри OLED</h4>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={temperatureData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="temperature" label={{ value: 'Температура, °C', position: 'insideBottom', offset: -5 }} />
              <YAxis yAxisId="left" label={{ value: 'Ефективність, %', angle: -90, position: 'insideLeft' }} />
              <YAxis yAxisId="right" orientation="right" label={{ value: 'Час життя, год', angle: 90, position: 'insideRight' }} />
              <Tooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="efficiency" stroke="#e91e63" strokeWidth={2} name="Ефективність" />
              <Line yAxisId="right" type="monotone" dataKey="lifetime" stroke="#9c27b0" strokeWidth={2} name="Час життя" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <p style={styles.paragraph}>
          З графіка видно експоненційне зменшення ефективності та часу життя з ростом температури. 
          При 150°C ефективність падає до 60% від початкової, а час життя скорочується у 5 разів.
        </p>
      </div>

      {/* CIE координати */}
      <div style={styles.section}>
        <h2 style={styles.h2}>6. Колориметрія</h2>
        <p style={styles.paragraph}>
          Координати колірності x = {calculations.x.toFixed(4)}, y = {calculations.y.toFixed(4)} 
          відповідають зеленому кольору з чистотою {(calculations.purity * 100).toFixed(1)}%. 
          Колірна температура CCT = {calculations.CCT} K.
        </p>
        
        <div style={styles.graph}>
          <h4 style={{ marginBottom: '15px', textAlign: 'center' }}>Графік 6. CIE 1931 Діаграма колірності</h4>
          <div style={{ position: 'relative', width: '100%', height: '550px' }}>
            <CIEDiagram 
              oledX={calculations.x} 
              oledY={calculations.y}
              whiteX={0.3127}
              whiteY={0.3290}
              dominantWavelength={520}
            />
          </div>
        </div>
        
        <p style={styles.paragraph}>
          На діаграмі CIE 1931 показано положення кольору OLED у просторі колірності. 
          Лінія від білої точки (D65) до точки джерела визначає чистоту кольору, 
          а перетин з межею спектрального локусу вказує на домінуючу довжину хвилі.
        </p>
      </div>

      {/* Висновки */}
      <div style={styles.section}>
        <h2 style={styles.h2}>Висновки</h2>
        <p style={styles.paragraph}>
          Досліджено OLED з зеленим випромінюванням. Світлова ефективність Kr = {calculations.Kr.toFixed(2)} лм/Вт 
          складає {((calculations.Kr / 683) * 100).toFixed(1)}% від теоретичного максимуму. Порогова напруга 
          включення ~2.6 В. Максимальна струмова ефективність досягається при помірних струмах.
        </p>
        <p style={styles.paragraph}>
          Температурні дослідження показали критичну залежність параметрів від температури. 
          Для забезпечення стабільної роботи необхідне ефективне тепловідведення.
        </p>
      </div>
    </div>
  );
};

export default OLEDConspectFull;
